import type { HookMap } from '@toast-ui/editor';
import { LocalizedText, useAppClockService } from '@mews/framework';
import { TextKey, SearchType } from '@mews-commander/enums';
import { Option as OptionType } from '@mews-commander/types';
import { getTodayStructuredDate } from '@mews-commander/legacy-entities/src/selectors/session/dateTime';
import { useCommanderSelector } from '@mews-commander/utils';
import { FieldsContainer, FormMeta, FormMetaSection } from '@mews-commander/ui';
import { DateTimePickerField, InputField, MarkdownEditorFormField, SelectField } from '@mews-commander/legacy-form';
import { SearchFormField } from '../../../components/form';
import { FormValues } from '../types';

export interface TasksFormFieldsProps {
    scope: string;
    departments?: Array<OptionType>;
    form: string;
    initialValues: FormValues;
    markdownEditorHooks: HookMap;
    taskMeta?: FormMeta;
}

export const TasksFormFields = ({
    departments,
    form,
    markdownEditorHooks,
    initialValues: { assigneeProfileId, serviceOrderId },
    taskMeta,
}: TasksFormFieldsProps) => {
    const appClockService = useAppClockService();
    const today = useCommanderSelector((state) => getTodayStructuredDate(state, { appClockService }));

    return (
        <FieldsContainer>
            <SearchFormField
                {...assigneeProps}
                preselectedResultId={assigneeProfileId}
                scope={`${form}-task-form-assigneeId`}
            />
            <SelectField {...departmentProps} options={departments} />
            <InputField {...taskNameProps} />
            <MarkdownEditorFormField markdownEditorHooks={markdownEditorHooks} {...descriptionProps} />

            <SearchFormField
                {...reservationProps}
                scope={`${form}-task-form-serviceOrderId`}
                preselectedResultId={serviceOrderId}
            />

            <DateTimePickerField form={form} minDate={today} {...dateTimeWrapperProps} />

            <FormMetaSection meta={taskMeta} />
        </FieldsContainer>
    );
};

const assigneeProps = {
    label: <LocalizedText textKey={TextKey.Assignee} />,
    name: 'assigneeProfileId',
    searchType: SearchType.EnterpriseProfile,
};

const departmentProps = {
    label: <LocalizedText textKey={TextKey.Department} />,
    name: 'departmentId',
    showEmptyValue: true,
};
const taskNameProps = {
    label: <LocalizedText textKey={TextKey.TaskName} />,
    name: 'name',
    required: true,
};
const descriptionProps = {
    label: <LocalizedText textKey={TextKey.Description} />,
    name: 'description',
};

const reservationProps = {
    label: <LocalizedText textKey={TextKey.Reservation} />,
    name: 'serviceOrderId',
    searchType: SearchType.Reservation,
};

const dateTimeWrapperProps = {
    label: <LocalizedText textKey={TextKey.Deadline} />,
    inlined: true,
    required: true,
};
